import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const SkillsStep = ({ formData, updateFormData, errors, onNext, onPrevious }) => {
  const [newSkill, setNewSkill] = useState('');
  const [skillLevel, setSkillLevel] = useState('intermediate');

  const skillLevels = [
    { value: 'beginner', label: 'Beginner (0-1 years)' },
    { value: 'intermediate', label: 'Intermediate (2-4 years)' },
    { value: 'advanced', label: 'Advanced (5-7 years)' },
    { value: 'expert', label: 'Expert (8+ years)' }
  ];

  const experienceLevels = [
    { value: 'entry', label: 'Entry Level (0-2 years)' },
    { value: 'mid', label: 'Mid Level (3-5 years)' },
    { value: 'senior', label: 'Senior Level (6-10 years)' },
    { value: 'lead', label: 'Lead/Principal (10+ years)' }
  ];

  const addSkill = () => {
    if (newSkill?.trim() && !formData?.skills?.some(skill => skill?.name?.toLowerCase() === newSkill?.toLowerCase())) {
      const updatedSkills = [...formData?.skills, {
        id: Date.now(),
        name: newSkill?.trim(),
        level: skillLevel
      }];
      updateFormData('skills', updatedSkills);
      setNewSkill('');
      setSkillLevel('intermediate');
    }
  };

  const removeSkill = (skillId) => {
    const updatedSkills = formData?.skills?.filter(skill => skill?.id !== skillId);
    updateFormData('skills', updatedSkills);
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter') {
      e?.preventDefault();
      addSkill();
    }
  };

  const getLevelColor = (level) => {
    const colors = {
      beginner: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      intermediate: 'bg-blue-100 text-blue-800 border-blue-200',
      advanced: 'bg-green-100 text-green-800 border-green-200',
      expert: 'bg-purple-100 text-purple-800 border-purple-200'
    };
    return colors?.[level] || colors?.intermediate;
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-semibold text-foreground mb-2">
          Skills & Experience
        </h2>
        <p className="text-muted-foreground">
          Tell us about your technical skills and professional experience
        </p>
      </div>
      <div className="space-y-6">
        {/* Desired Position */}
        <Input
          label="Desired Position"
          type="text"
          placeholder="e.g., Senior Frontend Developer, Data Scientist"
          description="What role are you looking for?"
          value={formData?.desiredPosition}
          onChange={(e) => updateFormData('desiredPosition', e?.target?.value)}
          error={errors?.desiredPosition}
          required
        />

        {/* Experience Level */}
        <Select
          label="Experience Level"
          description="Your overall professional experience"
          options={experienceLevels}
          value={formData?.experienceLevel}
          onChange={(value) => updateFormData('experienceLevel', value)}
          error={errors?.experienceLevel}
          required
        />

        {/* Skills Section */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Technical Skills *
          </label>
          <p className="text-sm text-muted-foreground mb-4">
            Add your technical skills and specify your proficiency level
          </p>

          {/* Add Skill Input */}
          <div className="flex gap-2 mb-4">
            <div className="flex-1">
              <Input
                type="text"
                placeholder="Enter a skill (e.g., React, Python, SQL)"
                value={newSkill}
                onChange={(e) => setNewSkill(e?.target?.value)}
                onKeyPress={handleKeyPress}
              />
            </div>
            <div className="w-48">
              <Select
                options={skillLevels}
                value={skillLevel}
                onChange={setSkillLevel}
                placeholder="Level"
              />
            </div>
            <Button
              variant="outline"
              iconName="Plus"
              onClick={addSkill}
              disabled={!newSkill?.trim()}
            >
              Add
            </Button>
          </div>

          {/* Skills List */}
          {formData?.skills?.length > 0 && (
            <div className="space-y-2 mb-4">
              <h4 className="text-sm font-medium text-foreground">Your Skills:</h4>
              <div className="flex flex-wrap gap-2">
                {formData?.skills?.map((skill) => (
                  <div
                    key={skill?.id}
                    className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm border ${getLevelColor(skill?.level)}`}
                  >
                    <span className="font-medium">{skill?.name}</span>
                    <span className="text-xs opacity-75">
                      {skillLevels?.find(level => level?.value === skill?.level)?.label?.split(' ')?.[0]}
                    </span>
                    <button
                      onClick={() => removeSkill(skill?.id)}
                      className="ml-1 hover:bg-black/10 rounded-full p-0.5"
                    >
                      <Icon name="X" size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {errors?.skills && (
            <p className="text-error text-sm">{errors?.skills}</p>
          )}
        </div>

        {/* Work Experience Description */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Professional Summary
          </label>
          <textarea
            placeholder="Briefly describe your professional background, key achievements, and what makes you unique..."
            value={formData?.professionalSummary}
            onChange={(e) => updateFormData('professionalSummary', e?.target?.value)}
            rows={4}
            className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none text-sm"
          />
          <p className="text-xs text-muted-foreground mt-1">
            {formData?.professionalSummary?.length}/500 characters
          </p>
        </div>
      </div>
      {/* Navigation */}
      <div className="flex justify-between mt-8">
        <Button variant="outline" onClick={onPrevious} iconName="ArrowLeft">
          Previous
        </Button>
        <Button
          onClick={onNext}
          iconName="ArrowRight"
          iconPosition="right"
          disabled={formData?.skills?.length === 0 || !formData?.desiredPosition || !formData?.experienceLevel}
        >
          Continue
        </Button>
      </div>
    </div>
  );
};

export default SkillsStep;